import React, { useState, useRef, useEffect } from 'react';
import LegendElement from './LegendElement';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import colors from '../../utils/colors';
import config from '../../utils/config';
import generateFilterOptions from '../../utils/filters/generateFilterOptions';
import { translate } from '../../utils/translationManager';
import { useLanguage } from '../../context/LanguageContext';
import './Legend.css'; // Import your CSS file

import useDeviceType from '../../hooks/useDeviceType';

const Legend = ({ layerName, layerLegend, isLayerVisible, handleApplyFilters, activeFilters, mainFilter }) => {
  const [isScrollable, setIsScrollable] = useState(false); // Tracks if the container is scrollable
  const scrollContainerRef = useRef(null);
  const { language } = useLanguage();
  const { isMobile } = useDeviceType();
  const filterOptions = mainFilter === 'building_group_type' 
    ? generateFilterOptions(config.filters.buildingTypesCategory)
    : mainFilter === 'rent_to_value_ratio_type'
    ? generateFilterOptions(config.filters.blockTypesCategory)
    : mainFilter === 'gfz_type'
    ? generateFilterOptions(config.filters.gfzTypesCategory)
    : mainFilter === 'property_value_change_type'
    ? generateFilterOptions(config.filters.propertyValueChangeCategory)
    : mainFilter === 'construction_year_type'
    ? generateFilterOptions(config.filters.constructionYearCategory)
    : mainFilter === 'grz_i_type'
    ? generateFilterOptions(config.filters.grzITypesCategory)
    : [];  // Default to an empty array or a suitable default if no conditions match

  useEffect(() => {
    if (scrollContainerRef.current) {
      setIsScrollable(scrollContainerRef.current.scrollWidth > scrollContainerRef.current.clientWidth);
    }
  }, [layerLegend]);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -100, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 100, behavior: 'smooth' });
  };

  const isFilterApplied = (activeFilters, filterKey, filterValue) => {
    if (!activeFilters || !activeFilters.filterRange) return true;
    const filter = activeFilters.filterRange.find(f => f.key === filterKey);
    return !filter || filter.value.includes(filterValue);
  };

  return (
    <>
      {isLayerVisible && (
        <div style={{
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          bottom: isMobile ? '8%' : '5%',
          width: '100%',
          padding: '0 10px',
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 'bold',
            marginBottom: '5px',
            padding: '5px 10px',
            color: colors.text.primary,
            background: colors.buttons.primary,
            boxShadow: '0 0px 5px rgba(0, 0, 0, 0.3)',
            borderRadius: 10,
          }}>
            {translate(layerName, language)}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 50, width: '100%' }}>
            {isScrollable && (
              <button onClick={scrollLeft} style={{
                backgroundColor: colors.buttons.primary,
                padding: '20px 10px',
                border: 'none',
                borderRadius: 10,
                cursor: 'pointer',
                marginRight: 5,
                fontSize: 16,
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.3)'
              }}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}

            <div
              className="filterScrollContainer"
              ref={scrollContainerRef}
              style={{
                display: 'flex',
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                maxWidth: 800,
                height: 60,
                backgroundColor: colors.buttons.primary,
                borderRadius: 10,
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.3)',
                whiteSpace: 'nowrap',
                alignItems: 'center',
              }}
            >
              {layerLegend.map((layer, index) => (
                <LegendElement
                  key={index}
                  filterKey={mainFilter}
                  applyFilter={handleApplyFilters}
                  label={translate(layer.key, language)}
                  value={layer.key}
                  options={filterOptions}
                  color={layer.color}
                  activeFilters={activeFilters}
                  isFilter={isFilterApplied(activeFilters, mainFilter, layer.key)}
                />
              ))}
            </div>

            {isScrollable && (
              <button onClick={scrollRight} style={{
                backgroundColor: colors.buttons.primary,
                padding: '20px 10px',
                border: 'none',
                borderRadius: 10,
                cursor: 'pointer',
                marginLeft: 5,
                fontSize: 16,
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.3)'
              }}>
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Legend;
